import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Title, ArrowRightIcon, ToggleArrow, Flex } from '@kvdbil/components';
import Link from '../../Link';
import { Collapse } from 'react-collapse';
import { FaqCategory, FaqQuestion } from '../cmsDataHandler';
import { useTranslation } from '~/Locale';
import { createFaqURL } from './FAQ';
import Media from '../../Media';
import { slugify } from '~/helpers/slugify';

const CategoriesContainer = styled.div<{ isOpen: boolean }>`
  padding-block: 0.5rem;
  border-bottom: ${({ isOpen, theme }) =>
    !isOpen ? css`1px dotted ${theme.colors.grayscaleToned.light2}` : 'none'};
`;

const HelpLinkListItem = styled.li`
  position: relative;
  border-bottom: 1px dotted ${({ theme }) => theme.colors.grayscaleToned.light2};
  padding: 0.5rem 0;
  cursor: pointer;
`;

const HelpLinkListLink = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HelpLinkListArrowIcon = styled.span`
  color: ${({ theme }) => theme.colors.info.dark};
  font-size: 0.75rem;
  margin-left: auto;
  padding-left: 1rem;
`;

const HelpCategoryContainer = styled.div`
  display: grid;
  grid-template-columns: max-content auto max-content;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
`;

const ImageWrapper = styled.div`
  width: 1.5rem;
  height: 1.5rem;
`;

interface Props {
  category: FaqCategory;
  onArticleClick: (question: FaqQuestion) => void;
}

export const FAQCategory = ({ category, onArticleClick }: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const ariaControls = `faq-${slugify(category?.name ?? '')}-collapse-content`;

  return (
    <CategoriesContainer isOpen={isOpen}>
      <HelpCategoryContainer
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-controls={ariaControls}
      >
        <ImageWrapper>
          <Media
            src={category?.icon?.url ?? ''}
            alt={category?.icon?.alt ?? ''}
            width={24}
            height={24}
          />
        </ImageWrapper>

        <Title as="h2">{category?.name ?? ''}</Title>

        <Flex grow={1} justify={'end'}>
          <ToggleArrow isExpanded={isOpen} />
        </Flex>
      </HelpCategoryContainer>
      <Collapse isOpened={isOpen}>
        <ul id={ariaControls} {...(!isOpen ? { inert: '' } : null)}>
          {category.questions.map((question, index) => (
            <HelpLinkListItem key={String(question?.slug) + String(index)}>
              <HelpLinkListLink
                to={createFaqURL(t, question?.slug ?? '')}
                onClick={() => onArticleClick(question)}
              >
                {question.question}

                <HelpLinkListArrowIcon>
                  <ArrowRightIcon />
                </HelpLinkListArrowIcon>
              </HelpLinkListLink>
            </HelpLinkListItem>
          ))}
        </ul>
      </Collapse>
    </CategoriesContainer>
  );
};

export default FAQCategory;
